import React, { useEffect, useState, useCallback, createContext } from 'react';
import Head from 'next/head';
import Header from '../components/Header';
import Footer from '../components/Footer';
// import CookieBanner from '../components/CookieBanner';
import CartModal from '../components/CartModal';
import TermsPopup from '../components/TermsPopup';
import useUserStore from '../store/userStore';
import MakeaLinkPopup from '../components/MakeaLinkPopup';
import AuthInitializer from '../components/AuthInitializer';
import { useServerAuth } from '../hooks/useServerAuth';
import { Unbounded } from 'next/font/google';
import authService from '@/services/authService';

const unbounded = Unbounded({
  subsets: ['latin', 'cyrillic'],
  display: 'swap',
  weight: ['300', '400', '500', '700'],
  variable: '--font-unbounded',
}); 

export const TermsContext = createContext(null);

function BaseLayout({
  title,
  description = '',
  showHeader = true,
  canonicalUrl,
  searchQuery = '',
  mediaType = '',
  children,
  isLoginPage = false,
  // Данные авторизации с сервера
  serverAuthState = null
}) {
  const [currentUrl, setCurrentUrl] = useState(canonicalUrl || '');
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [onAcceptCallback, setOnAcceptCallback] = useState(null);
  const [showMaintenanceNotice, setShowMaintenanceNotice] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);

  // Синхронизируем серверные данные авторизации с клиентским хранилищем
  const { isAuthenticated, userData } = useServerAuth(
    serverAuthState?.isAuthenticated, 
    serverAuthState?.user
  );

  const { 
    termsAccepted,
    acceptTerms,
    isLoggedIn,
    user,
    setUserManually
  } = useUserStore();

  useEffect(() => {
    if (!canonicalUrl && typeof window !== 'undefined') {
      setCurrentUrl(window.location.href);
    }
  }, [canonicalUrl]);

  // Проверка токенов при каждой загрузке страницы
  useEffect(() => {
    const checkAuth = async () => {
      if (!isLoggedIn && !authChecked) {
        try {
          // Получаем токен и проверяем его валидность
          const token = await authService.getValidToken();
          if (token) {
            // Если токен валидный, запрашиваем статус авторизации
            const authStatus = await authService.checkAuthStatus();
            if (authStatus.authenticated && authStatus.user) {
              // Если авторизация подтверждена, устанавливаем пользователя
              setUserManually(authStatus.user);
            }
          }
        } catch (error) {
          console.error('Error checking auth in BaseLayout:', error);
        } finally {
          setAuthChecked(true);
        }
      }
    };

    checkAuth();
  }, [isLoggedIn, authChecked, setUserManually]);

  const handleClose = () => {
    setIsTermsOpen(false);
  };

  const handleAccept = async () => {
    try {
      acceptTerms();
      
      if (isLoggedIn && user) {
        await fetch(`${process.env.NEXT_PUBLIC_BACK_URL}/terms/accept`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        });
      }
    } catch (error) {
      console.error('Error accepting terms:', error);
    }

    setIsTermsOpen(false);
    if (onAcceptCallback) {
      onAcceptCallback();
      setOnAcceptCallback(null);
    }
  };

  const requestTerms = useCallback((callback) => {
    setOnAcceptCallback(() => callback);
    setIsTermsOpen(true);
  }, []);

  return (
    <div className={`flex flex-col ${unbounded.variable} ${isLoginPage ? 'h-screen' : 'min-h-screen'}`}>
      <Head>
        <meta charSet="UTF-8" />
        <title>{title}</title>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <link rel="icon" href="/favicon.svg" type="image/svg+xml" />
        {description && <meta name="description" content={description} />}
        
        <link
          rel="preconnect"
          href="https://fonts.googleapis.com"
          crossOrigin="anonymous"
        />
        <link 
          rel="preconnect" 
          href="https://fonts.gstatic.com" 
          crossOrigin="anonymous" 
        />
      </Head>

      {/* Всегда включаем AuthInitializer для проверки авторизации */}
      <AuthInitializer />

      {showHeader && (
        <>
          <TermsPopup isOpen={isTermsOpen} onClose={handleClose} onAccept={handleAccept} />
          <MakeaLinkPopup />
          <Header searchQuery={searchQuery} mediaType={mediaType} serverAuthState={serverAuthState} />
        </>
      )}

      <TermsContext.Provider value={{ requestTerms }}>
        <main className={`flex-grow flex flex-col ${isLoginPage ? 'h-full' : ''} ${showHeader ? 'pt-20 md:pt-0' : ''}`}>
          {children}
        </main>
      </TermsContext.Provider>

      <Footer />
      {/* <CookieBanner /> */}
      
{/*       
      <div className="fixed bottom-0 left-0 w-full bg-black py-4 text-center z-[9999]">
        <div className="text-white font-medium text-lg px-4">
          We are currently performing maintenance work. Some services may be temporarily unavailable.
        </div>
      </div>
      */}
    </div>
  );
}

export default BaseLayout;